'use client'

import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { ApiError } from 'aws-amplify/api'
import { ProtectedAttribute } from '@/components/protected-attribute'
import { ATTRIBUTES } from '@/lib/rbac/constants'
import type { BudgetFormData, BudgetMetadata } from '@/types/budgets'
import { formatCurrency } from '@/types/budgets'
import { updateClientBudget } from '@/lib/services/budgets.service'
import { useToast } from '@/hooks/use-toast'

/**
 * Zod validation schema for budget form fields
 *
 * Rules:
 * - All fields are optional (nullable and undefined)
 * - All fields must be numeric
 * - All fields must be >= 0 EXCEPT targetMargin (can be negative in financial calculations)
 */
const budgetSchema = z.object({
  targetRevenue: z.union([
    z.number().min(0, 'Must be greater than or equal to 0'),
    z.null(),
    z.undefined(),
  ]),
  targetCosts: z.union([
    z.number().min(0, 'Must be greater than or equal to 0'),
    z.null(),
    z.undefined(),
  ]),
  targetTax: z.union([
    z.number().min(0, 'Must be greater than or equal to 0'),
    z.null(),
    z.undefined(),
  ]),
  targetMargin: z.union([z.number(), z.null(), z.undefined()]), // Can be negative
  labourCost: z.union([
    z.number().min(0, 'Must be greater than or equal to 0'),
    z.null(),
    z.undefined(),
  ]),
  approvedWork: z.union([
    z.number().min(0, 'Must be greater than or equal to 0'),
    z.null(),
    z.undefined(),
  ]),
})

/**
 * Props interface for BudgetSettingsForm component
 */
export interface BudgetSettingsFormProps {
  clientCode: string
  year: number
  initialValues: BudgetFormData
  metadata?: BudgetMetadata
  onSave: (values: BudgetFormData) => Promise<void>
  className?: string
}

/**
 * Read-only budget view for non-directors
 *
 * Displays all budget fields as formatted currency values without edit capability
 */
const ReadOnlyBudgetView: React.FC<{
  values: BudgetFormData
  metadata?: BudgetMetadata
}> = ({ values, metadata }) => {
  const fields = [
    { label: 'Receita Projetada (PROA)', value: values.targetRevenue },
    { label: 'Custos Projetados', value: values.targetCosts },
    { label: 'Impostos Projetados', value: values.targetTax },
    { label: 'Margem Projetada', value: values.targetMargin },
    { label: 'Folha de Pagamento', value: values.labourCost },
    { label: 'Encarteirado', value: values.approvedWork },
  ]

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {fields.map((field) => (
          <div key={field.label}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(field.value)}
            </div>
          </div>
        ))}
      </div>

      {metadata && (metadata.lastUpdated || metadata.updatedBy) && (
        <div className="text-sm text-gray-500 border-t pt-4">
          <p>
            {metadata.lastUpdated && (
              <>
                Última atualização:{' '}
                {new Intl.DateTimeFormat('pt-BR', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                }).format(new Date(metadata.lastUpdated))}
              </>
            )}
            {metadata.updatedBy && (
              <> por {metadata.updatedBy}</>
            )}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Currency input field component
 *
 * Handles BRL currency formatting and numeric conversion
 */
const CurrencyInput: React.FC<{
  label: string
  name: string
  value: number | null | undefined
  onChange: (value: number | null | undefined) => void
  onBlur: () => void
  error?: string
  disabled?: boolean
  helpText?: string
  allowNegative?: boolean
  ariaLabel?: string
}> = ({ label, name, value, onChange, onBlur, error, disabled, helpText, allowNegative = false, ariaLabel }) => {
  const [displayValue, setDisplayValue] = useState<string>(
    value !== null && value !== undefined ? value.toString() : ''
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value

    // Allow empty string
    if (rawValue === '') {
      setDisplayValue('')
      onChange(null)
      return
    }

    // Remove non-numeric characters except minus sign and decimal point
    const cleanedValue = rawValue.replace(/[^\d.-]/g, '')

    // Check if value starts with minus
    const isNegative = cleanedValue.startsWith('-')

    // For validation purposes, allow the value to be set even if negative
    // The form validation will catch it and show an error

    // Parse to number
    const numericValue = parseFloat(cleanedValue)

    if (!isNaN(numericValue)) {
      setDisplayValue(cleanedValue)
      onChange(numericValue)
    } else if (cleanedValue === '-' && allowNegative) {
      // Allow typing just the minus sign
      setDisplayValue('-')
    } else if (cleanedValue === '-') {
      // Allow typing minus for validation to catch it
      setDisplayValue('-')
    }
  }

  const handleBlur = () => {
    // Format display value on blur
    if (value !== null && value !== undefined && !isNaN(value)) {
      setDisplayValue(value.toString())
    }
    onBlur()
  }

  const errorId = error ? `${name}-error` : undefined

  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {helpText && (
        <p className="text-xs text-gray-500 mb-2">{helpText}</p>
      )}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          R$
        </span>
        <input
          id={name}
          name={name}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label={ariaLabel || label}
          aria-describedby={errorId}
          aria-invalid={error ? 'true' : 'false'}
          className={`
            block w-full pl-10 pr-3 py-2 rounded-md border transition-colors duration-200
            focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus-visible:outline-none
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${error ? 'border-red-500' : 'border-gray-300'}
          `}
          placeholder="0,00"
        />
      </div>
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * BudgetSettingsForm Component
 *
 * Form for editing client budget settings with 6 currency input fields.
 * Features:
 * - React Hook Form integration for state management
 * - Zod validation schema with custom rules
 * - BRL currency formatting
 * - RBAC protection (director-only access)
 * - Read-only view for non-directors
 * - Save/Cancel buttons with proper state management
 * - Success/error toast notifications
 * - Comprehensive error handling with field highlighting
 * - Unsaved changes warning on page unload
 * - Audit metadata display
 * - Disabled state during save operations
 */
export const BudgetSettingsForm: React.FC<BudgetSettingsFormProps> = ({
  clientCode,
  year,
  initialValues,
  metadata: initialMetadata,
  onSave,
  className = '',
}) => {
  const [metadata, setMetadata] = useState<BudgetMetadata | undefined>(initialMetadata)
  const { success, error: showErrorToast } = useToast()

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<BudgetFormData>({
    resolver: zodResolver(budgetSchema),
    defaultValues: initialValues,
    mode: 'onBlur', // Validate on blur per AC5 requirement
  })

  // Unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = '' // Chrome requires returnValue to be set
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const onSubmit = async (data: BudgetFormData) => {
    try {
      // Call the API to update client budget
      const response = await updateClientBudget(clientCode, year, data)

      // Update metadata with response from API
      setMetadata(response.metadata)

      // Show success toast
      success('Configurações salvas com sucesso', { duration: 5000 })

      // Call parent onSave callback
      await onSave(data)

      // Reset form dirty state to match new values
      reset(data)
    } catch (err) {
      // Handle API errors
      handleApiError(err)
    }
  }

  const handleApiError = (err: unknown) => {
    // Check if it's an Amplify REST API error
    if (err && typeof err === 'object' && 'response' in err) {
      const apiError = err as ApiError

      // Parse error response body
      const parseErrorBody = () => {
        try {
          const body = apiError.response?.body
          if (typeof body === 'string') {
            const errorBody = JSON.parse(body)
            return errorBody?.error
          }
          return null
        } catch {
          return null
        }
      }

      const errorData = parseErrorBody()
      if (!errorData) {
        // Network error or no response
        showErrorToast('Erro de conexão. Tente novamente.', { duration: 5000 })
        return
      }

      const errorCode = errorData.code

      switch (errorCode) {
          case 'VALIDATION_ERROR':
            // Highlight specific fields with errors
            if (errorData.details) {
              Object.entries(errorData.details).forEach(([field, message]) => {
                setError(field as keyof BudgetFormData, {
                  message: message as string,
                })
              })
            }
            showErrorToast(errorData.message || 'Erro de validação. Verifique os campos.', {
              duration: 5000,
            })
            break

          case 'FORBIDDEN':
            showErrorToast('Você não tem permissão para editar configurações de orçamento', {
              duration: 5000,
            })
            break

          case 'NOT_FOUND':
            showErrorToast('Orçamento não encontrado para este cliente e ano', {
              duration: 5000,
            })
            break

          case 'CONFLICT':
            showErrorToast('O orçamento foi modificado por outro usuário. Recarregue a página.', {
              duration: 5000,
            })
            break

          default:
            showErrorToast('Erro ao salvar. Tente novamente mais tarde.', {
              duration: 5000,
            })
      }
    } else {
      // Generic error
      showErrorToast('Erro inesperado. Tente novamente.', { duration: 5000 })
    }
  }

  const handleCancel = () => {
    reset(initialValues)
  }

  const budgetFields = [
    {
      name: 'targetRevenue' as const,
      label: 'Receita Projetada (PROA)',
      helpText: 'Receita projetada para o ano',
      ariaLabel: 'Receita projetada PROA em reais brasileiros para o ano selecionado',
    },
    {
      name: 'targetCosts' as const,
      label: 'Custos Projetados',
      helpText: 'Custos operacionais projetados para o ano',
      ariaLabel: 'Custos operacionais projetados em reais brasileiros para o ano selecionado',
    },
    {
      name: 'targetTax' as const,
      label: 'Impostos Projetados',
      helpText: 'Obrigações fiscais projetadas para o ano',
      ariaLabel: 'Impostos e obrigações fiscais projetados em reais brasileiros para o ano selecionado',
    },
    {
      name: 'targetMargin' as const,
      label: 'Margem Projetada',
      helpText: 'Margem de lucro projetada (pode ser negativa)',
      allowNegative: true,
      ariaLabel: 'Margem de lucro projetada em reais brasileiros para o ano selecionado, pode ser negativa',
    },
    {
      name: 'labourCost' as const,
      label: 'Folha de Pagamento',
      helpText: 'Custos de alocação de mão de obra por cliente',
      ariaLabel: 'Custos de folha de pagamento e alocação de mão de obra em reais brasileiros para o ano selecionado',
    },
    {
      name: 'approvedWork' as const,
      label: 'Encarteirado',
      helpText: 'Valor de trabalho aprovado por cliente',
      ariaLabel: 'Valor de trabalho aprovado encarteirado em reais brasileiros para o ano selecionado',
    },
  ]

  return (
    <ProtectedAttribute
      attributeName={ATTRIBUTES.FINANCIAL_SETTINGS}
      action="edit"
      fallback={<ReadOnlyBudgetView values={initialValues} metadata={metadata} />}
    >
      <form onSubmit={handleSubmit(onSubmit)} className={`space-y-6 ${className}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {budgetFields.map((field) => (
            <Controller
              key={field.name}
              name={field.name}
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <CurrencyInput
                  label={field.label}
                  name={field.name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={errors[field.name]?.message}
                  disabled={isSubmitting}
                  helpText={field.helpText}
                  allowNegative={field.allowNegative}
                  ariaLabel={field.ariaLabel}
                />
              )}
            />
          ))}
        </div>

        {metadata && (metadata.lastUpdated || metadata.updatedBy) && (
          <div className="text-sm text-gray-500 border-t pt-4">
            <p>
              {metadata.lastUpdated && (
                <>
                  Última atualização:{' '}
                  {new Intl.DateTimeFormat('pt-BR', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(metadata.lastUpdated))}
                </>
              )}
              {metadata.updatedBy && (
                <> por {metadata.updatedBy}</>
              )}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={handleCancel}
            disabled={!isDirty}
            className={`
              px-4 py-2 rounded-md font-medium transition-colors duration-200
              border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2
              ${
                !isDirty
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!isDirty || !isValid || isSubmitting}
            className={`
              px-4 py-2 rounded-md font-medium transition-colors duration-200
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2
              ${
                !isDirty || !isValid || isSubmitting
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Salvando...
              </span>
            ) : (
              'Salvar Alterações'
            )}
          </button>
        </div>
      </form>
    </ProtectedAttribute>
  )
}
